import { useMemo, useState, type FormEvent } from "react";

import { Check, CircleCheckBig, Loader2, X } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { CodeInput } from "@/components/auth/CodeInput";
import { GradientScene, SceneError } from "@/components/auth/GradientScene";
import { ApiError } from "@/lib/api-client";

const RULES: { label: string; test: (v: string) => boolean }[] = [
  { label: "At least 12 characters", test: (v) => v.length >= 12 },
  { label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { label: "One number", test: (v) => /\d/.test(v) },
];

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [totpCode, setTotpCode] = useState("");
  // The server only asks for a second factor once it knows the account has one, so this
  // starts false and is switched on by a TOTP_REQUIRED response.
  const [needsTotp, setNeedsTotp] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [details, setDetails] = useState<string[]>();
  const [busy, setBusy] = useState(false);

  const results = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(password) })), [password]);
  const allMet = results.every((r) => r.ok);
  const matches = password.length > 0 && password === confirm;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setDetails(undefined);
    if (!matches) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/internal/v1/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          newPassword: password,
          ...(needsTotp ? { totpCode } : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        details?: string[];
      };

      if (!res.ok) {
        // ⚠ A reset link proves control of a mailbox and nothing more. Where the account
        // has a second factor the server insists on it, and says so with this code.
        if (data.error === "TOTP_REQUIRED") {
          setNeedsTotp(true);
          setError("This account uses two-factor authentication. Enter the code from your app.");
          setBusy(false);
          return;
        }
        throw new ApiError(res.status, data.message ?? res.statusText, data.details);
      }

      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setDetails(err.details);
      } else {
        setError("Could not reach the server. Check your connection and try again.");
      }
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <GradientScene>
        <h2 className="text-xl font-bold text-white">That link is not complete</h2>
        <p className="text-sm text-white/65">
          The reset link is missing its token. Request a new one — links are good for 24 hours.
        </p>
        <Link
          to="/forgot-password"
          className="gradient-btn block rounded-lg py-2.5 text-center text-sm font-semibold shadow-lg"
        >
          Request a new link
        </Link>
      </GradientScene>
    );
  }

  if (done) {
    return (
      <GradientScene>
        <div className="flex justify-center">
          <div className="rounded-full bg-white/15 p-3">
            <CircleCheckBig className="h-7 w-7 text-white" />
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold text-white">Password reset</h2>
          <p className="mt-2 text-sm text-white/65">
            You can now sign in with your new password.
          </p>
        </div>
        <Link
          to="/"
          className="gradient-btn block rounded-lg py-2.5 text-center text-sm font-semibold shadow-lg"
        >
          Sign in
        </Link>
      </GradientScene>
    );
  }

  return (
    <GradientScene>
      <div>
        <h2 className="text-xl font-bold text-white">Choose a new password</h2>
      </div>

      <form className="space-y-4" onSubmit={onSubmit}>
        <SceneError message={error} details={details} />

        <div>
          <label htmlFor="rp-password" className="mb-1.5 block text-sm font-medium text-white/80">
            New password
          </label>
          <input
            id="rp-password"
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="glass-input w-full rounded-lg px-4 py-2.5 text-sm"
          />
        </div>

        <ul className="space-y-1">
          {results.map((r) => (
            <li
              key={r.label}
              className={`flex items-center gap-2 text-xs ${r.ok ? "text-white/80" : "text-white/45"}`}
            >
              {r.ok ? <Check className="h-3.5 w-3.5 shrink-0" /> : <X className="h-3.5 w-3.5 shrink-0" />}
              {r.label}
            </li>
          ))}
        </ul>

        <div>
          <label htmlFor="rp-confirm" className="mb-1.5 block text-sm font-medium text-white/80">
            Confirm password
          </label>
          <input
            id="rp-confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="glass-input w-full rounded-lg px-4 py-2.5 text-sm"
          />
        </div>

        {needsTotp && (
          <div className="space-y-2 border-t border-white/15 pt-4">
            <p className="text-center text-sm text-white/70">
              Enter the six-digit code from your authenticator.
            </p>
            <CodeInput value={totpCode} onChange={setTotpCode} disabled={busy} autoFocus />
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !allMet || !matches || (needsTotp && totpCode.length !== 6)}
          className="gradient-btn flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Saving…" : "Reset password"}
        </button>
      </form>

      <Link
        to="/"
        className="block text-center text-xs text-white/50 underline underline-offset-2 hover:text-white/80"
      >
        Back to sign in
      </Link>
    </GradientScene>
  );
}
